import { FC, useMemo, useState } from 'react'
import { createColumnHelper, SortingState } from '@atbay/cyber-components'
import { SettingsTeamModule } from '@core/modules/team/team.module'
import { getTeamMemberDisplayName } from '@core/modules/team/team.utils'
import { useCustomTheme } from '@/core/theme'
import Typography from '@/ui/components/typography'
import Icon from '@components/icons/base-icon'
import { Switch } from '@components/buttons/switch'
import { StyledCustomTable } from '@/ui/components/CustomTable/CustomTable.styles'
import TableEmpty from '@/ui/components/table/table-empty'
import TeamRowActions from './TeamRowActions'
import { Cell, NameCell, ActionsCell } from './TeamTable.styles'
import UserTeamIcon from '@assets/images/user-team-icon.svg?react'

const columnHelper = createColumnHelper<TTeamMember>()

export interface TeamTableProps {
    data: TTeamMember[]
}

const TeamTable: FC<TeamTableProps> = ({ data }) => {
    const theme = useCustomTheme()
    const [sorting, setSorting] = useState<SortingState>([])

    const columns = useMemo(
        () => [
            {
                ...columnHelper.accessor('first_name', {
                    id: 'name',
                    header: () => (
                        <Typography variant="s-regular">Name</Typography>
                    ),
                    cell: (info) => (
                        <NameCell data-testid="user-name">
                            <Icon>
                                <UserTeamIcon />
                            </Icon>
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                color={theme.colors.greys[800]}
                                testId="user-name-text"
                            >
                                {getTeamMemberDisplayName(info.row.original) ||
                                    '-'}
                            </Typography>
                        </NameCell>
                    ),
                    enableSorting: true,
                }),
                width: '22%',
            },
            {
                ...columnHelper.accessor('email', {
                    id: 'email',
                    header: () => (
                        <Typography variant="s-regular">Email</Typography>
                    ),
                    cell: (info) => (
                        <Cell data-testid="user-email">
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                color={theme.colors.greys[700]}
                            >
                                {info.getValue() || '-'}
                            </Typography>
                        </Cell>
                    ),
                    enableSorting: true,
                }),
                width: '30%',
            },
            {
                ...columnHelper.accessor('role', {
                    id: 'role',
                    header: () => (
                        <Typography variant="s-regular">Role</Typography>
                    ),
                    cell: (info) => (
                        <Cell data-testid="user-role">
                            <Typography
                                className="table-cell-text capitalize"
                                variant="s-regular"
                                color={theme.colors.greys[700]}
                                textTransformRule="capitalize"
                            >
                                {info.getValue() || '-'}
                            </Typography>
                        </Cell>
                    ),
                    enableSorting: true,
                }),
                width: '16%',
            },
            {
                ...columnHelper.accessor('mfa_active', {
                    id: 'mfa',
                    header: () => (
                        <Typography variant="s-regular">MFA</Typography>
                    ),
                    cell: (info) => (
                        <Cell data-testid="user-mfa">
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                color={theme.colors.greys[700]}
                            >
                                {info.getValue() ? 'Yes' : 'No'}
                            </Typography>
                        </Cell>
                    ),
                    enableSorting: true,
                }),
                width: '10%',
            },
            {
                ...columnHelper.accessor('send_alerts', {
                    id: 'send_alerts',
                    header: () => (
                        <Typography variant="s-regular">
                            Email Alerts
                        </Typography>
                    ),
                    cell: (info) => (
                        <Cell data-testid="email-alerts">
                            <Switch
                                testId="toggle"
                                checked={info.getValue()}
                                onChange={(e: TToggleInput) => {
                                    void SettingsTeamModule.handleEmailAlerts(
                                        e.target.checked,
                                        info.row.original.user_id
                                    )
                                }}
                            />
                        </Cell>
                    ),
                    enableSorting: false,
                }),
                width: '14%',
            },
            {
                ...columnHelper.display({
                    id: 'actions',
                    header: () => null,
                    cell: (info) => (
                        <ActionsCell>
                            <TeamRowActions user={info.row.original} />
                        </ActionsCell>
                    ),
                }),
                width: '8%',
            },
        ],
        [theme]
    )

    return (
        <StyledCustomTable
            getRowId={(row: TTeamMember) => row.user_id}
            tableHeight={700}
            columns={columns}
            data={data}
            sorting={sorting}
            setSorting={setSorting}
            emptyState={<TableEmpty message="No team members found" />}
        />
    )
}

export default TeamTable
